import { DeleteOutline } from '@mui/icons-material';
import { Box, Grid, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import AddMoreButton from './AddMoreButton';
import FormVendorField from './FormVendorField';
import { createEmptyVendor } from './vendorFieldDefaults';

FormVendorFieldWithAddMore.propTypes = {
  fieldName: PropTypes.string,
  fieldAttributes: PropTypes.object,
  contractFileTypeId: PropTypes.number,
  invoiceFileTypeId: PropTypes.number,
};

VendorRow.propTypes = {
  rowIndex: PropTypes.number,
  fieldName: PropTypes.string,
  fieldAttributes: PropTypes.object,
  contractFileTypeId: PropTypes.number,
  invoiceFileTypeId: PropTypes.number,
  removeRow: PropTypes.func,
  canRemove: PropTypes.bool,
};

function VendorRow({
  rowIndex,
  fieldName,
  fieldAttributes,
  contractFileTypeId,
  invoiceFileTypeId,
  removeRow,
  canRemove,
}) {
  return (
    <Box display="flex" alignItems="flex-start">
      <Box flex={1} minWidth={0}>
        {' '}
        <FormVendorField
          fieldName={`${fieldName}[${rowIndex}]`}
          fieldAttributes={fieldAttributes}
          contractFileTypeId={contractFileTypeId}
          invoiceFileTypeId={invoiceFileTypeId}
        />
      </Box>
      {canRemove && (
        <Box pt={1}>
          <IconButton
            onClick={() => {
              removeRow(rowIndex);
            }}
            disabled={!fieldAttributes?.isEditable}
            color="error"
          >
            <DeleteOutline />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

export default function FormVendorFieldWithAddMore({
  fieldName,
  fieldAttributes,
  contractFileTypeId,
  invoiceFileTypeId,
}) {
  const { control, getValues, watch } = useFormContext();

  const vendorRowsMethods = useFieldArray({
    control,
    name: fieldName,
    keyName: '_id',
  });

  const vendorRows = watch(fieldName) ?? [];

  const visibleRows = vendorRowsMethods.fields
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ rowIndex }) => !vendorRows?.[rowIndex]?.vendors?.[0]?.deleteFlag);

  const getVendorTypeId = useCallback(
    () => getValues(`${fieldName}[0].vendors.0.vendorTypeId`) ?? null,
    [fieldName, getValues]
  );

  const createNewRow = (vendorTypeId = null) => ({
    vendors: [
      createEmptyVendor({
        vendorTypeId,
        allowMultipleVendors: true,
      }),
    ],
  });

  const softDeleteRow = (rowIndex) => {
    const row = getValues(`${fieldName}[${rowIndex}]`);
    const rowVendorId = row?.vendors?.[0]?.id ?? 0;

    if (rowVendorId > 0) {
      vendorRowsMethods.update(rowIndex, {
        ...row,
        vendors: [
          {
            ...(row?.vendors?.[0] ?? {}),
            deleteFlag: true,
          },
        ],
      });
      return;
    }

    vendorRowsMethods.remove(rowIndex);
  };

  return (
    <Grid>
      {visibleRows.map(({ row, rowIndex }) => (
        <VendorRow
          key={row._id}
          rowIndex={rowIndex}
          fieldName={fieldName}
          fieldAttributes={fieldAttributes}
          contractFileTypeId={contractFileTypeId}
          invoiceFileTypeId={invoiceFileTypeId}
          removeRow={softDeleteRow}
          canRemove={visibleRows.length > 1}
        />
      ))}

      <Box mt={1}>
        <AddMoreButton
          buttonLabel="Add more"
          handleAddMoreClick={() => {
            vendorRowsMethods.append(createNewRow(getVendorTypeId()));
          }}
          disabled={!fieldAttributes?.isEditable}
        />
      </Box>
    </Grid>
  );
}
